import { useEffect, useState } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { ChoiceField } from "../components/Fields";
import { I } from "../components/icons";
import { NavRow, RowGroup } from "../components/Rows";
import { Segmented } from "../components/Segmented";
import { Screen } from "../components/Shell";
import { ago } from "../components/StepsRefresh";
import { SyncChip } from "../components/SyncChip";
import { TopBar } from "../components/TopBar";
import { cycleInfo, cycleOn } from "../domain/cycle";
import { addDays, todayStr } from "../domain/date";
import { curWeight } from "../domain/energy";
import { older, periodsStopped, proteinRule } from "../domain/eating";
import { deficitTarget, foodOn } from "../domain/intake";
import { phase, weekNo } from "../domain/plan";

import { hasTreadmill } from "../domain/treadmill";
import { remindersSummary } from "../domain/water";
import type { KayaState, Profile as ProfileT } from "../domain/types";
import { useDeviceAnswers, useIsAdmin, useKaya } from "../store/kayaStore";
import { useLinks } from "../lib/linked";
import { TEXT_SIZE_LABEL } from "../lib/textSize";
import { api } from "../sync/api";
import "./You.css";

/**
 * The You tab: settings and account, as rows that each show the value they hold.
 *
 * Most visits are to *check* a setting, not change it, so the value on the row answers without
 * opening anything (ADR-018). Appearance is the one control drawn inline: its result is visible
 * the instant you press it, so making you navigate to see it would be perverse.
 */

/** Did a phone actually post steps recently? Read off state rather than asking the server, so the
 *  hub never waits on a request to render a row. */
function phoneCounting(S: KayaState): boolean {
  const since = addDays(todayStr(), -3);
  return Object.entries(S.logs).some(
    ([ds, L]) => ds >= since && typeof L.deviceSteps === "number" && L.deviceSteps > 0
  );
}

/** The latest reading — the figure Progress leads with — falling back to the starting weight. */
function nowWeight(S: KayaState): number {
  return curWeight(S);
}

function goalLine(p: ProfileT, weight: number): string {
  if (p.goal === "weight")
    return p.targetWeight
      ? `${weight.toFixed(1)} kg now · target ${p.targetWeight} kg`
      : `${weight.toFixed(1)} kg now`;
  return p.targetWaist ? `Waist target ${p.targetWaist} cm` : "Trim waist";
}

export default function You() {
  const { S, update, user, viewAsUser, setViewAsUser, stepsReportedAt, syncStatus, online, meta } = useKaya();
  const device = useDeviceAnswers();
  const isAdmin = useIsAdmin();
  const p = S.profile;
  const treadmill = hasTreadmill(S);
  const programCount = p.programs?.length ?? 0;
  const [people, setPeople] = useState<{ all: number; waiting: number } | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    let live = true;
    api
      .adminUsers()
      .then(({ users }) => {
        if (live)
          setPeople({ all: users.length, waiting: users.filter((u) => u.status === "pending").length });
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [isAdmin]);

  const ci = cycleOn(S) ? cycleInfo(S) : null;
  const cycleValue = periodsStopped(S)
    ? "Periods have stopped"
    : !cycleOn(S)
    ? "Off"
    : ci?.day
      ? `On · day ${ci.day} of ~${S.cycle.length || 28}`
      : "On";

  const sessionValue =
    (p.phaseMode === "auto"
      ? `Week ${weekNo(S)} · phase ${phase(S) + 1}`
      : `Phase ${Number(p.phaseMode) + 1} pinned`) + (p.hipSafe ? " · hip-safe on" : "");

  // the programme and how far "Plan my exercise" got (ADR-053)
  const trainingValue = `${(p.training?.programme ?? (older(S) ? "steady" : "kaya")) === "steady" ? "Steady" : "Kaya"}${
    p.training?.at !== undefined ? "" : " · Plan my exercise not answered yet"
  }`;

  const counting = phoneCounting(S);
  const reported = ago(stepsReportedAt);
  const stepsValue = counting ? `Counting${reported ? ` · ${reported}` : ""}` : "Not set up";

  const alertsValue = p.remindOn
    ? `Reminders ${p.remindMorning} and ${p.remindEvening}`
    : p.notifyWanted
      ? "Notifications on"
      : "Off";

  const syncValue =
    !online || syncStatus === "offline"
      ? "Offline"
      : syncStatus === "error"
        ? "Sync failed"
        : syncStatus === "syncing"
          ? "Syncing…"
          : "Up to date";

  const theme = p.theme || "system";

  // Links are read from the server (ADR-046); offline the rows keep their plain words.
  const { links } = useLinks();
  const nameOf = (l: NonNullable<typeof links>[number]) =>
    (l.role === "reader" && l.label) || l.person?.name || l.person?.email || "Someone";
  const asking = (links ?? []).find((l) => l.role === "subject" && l.status === "pending");
  const reading = (links ?? []).filter((l) => l.role === "reader");
  const readingOn = reading.filter((l) => l.status === "active").map(nameOf);
  const readingWait = reading.length - readingOn.length;
  const familyValue =
    readingOn.length || readingWait
      ? [readingOn.join(", "), readingWait ? `${readingWait} waiting` : ""].filter(Boolean).join(" · ")
      : "Read a parent’s plan and answer for them";

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar
            title={p.name || user?.name || "You"}
            sub={user?.email}
            action={
              <span className="you-head-tag">
                <SyncChip />
              </span>
            }
          />

          <div className="you-page">
            <RowGroup heading="You">
              <NavRow icon={I.scale} title="Body & goals" subtitle={goalLine(p, nowWeight(S))} to="/you/body" />
              <NavRow
                icon={I.bowl}
                title="Food & calories"
                subtitle={
                  foodOn(S)
                    ? `${deficitTarget(S, undefined, device)} under Out · ${proteinRule(S).perKg} g/kg protein`
                    : "Off"
                }
                to="/you/food"
              />
              <NavRow icon={I.drop} tone="cycle" title="Cycle" subtitle={cycleValue} to="/you/cycle" />
            </RowGroup>

            <RowGroup heading="Training">
              <NavRow icon={I.list} title="Training" subtitle={trainingValue} to="/you/training" />
              <NavRow icon={I.dumbbell} title="Session" subtitle={sessionValue} to="/you/session" />
              <NavRow
                icon={I.treadmill}
                title="Treadmill"
                subtitle={
                  treadmill
                    ? `${p.brisk} / ${p.easy} km/h · ${p.incline ? `${p.incline}% incline` : "no incline"}`
                    : "Not using one"
                }
                to="/you/treadmill"
              />
              {/* Programs are machine programs; with no treadmill there is nothing to run them on
                  (ADR-022). */}
              {treadmill ? (
                <NavRow
                  icon={I.list}
                  title="Programs"
                  subtitle={programCount ? `${programCount} saved` : "None yet"}
                  to="/train/programs"
                />
              ) : null}
            </RowGroup>

            <RowGroup heading="Phone">
              <NavRow
                icon={I.footsteps}
                tone={counting ? "success" : "default"}
                title="Step counting"
                subtitle={stepsValue}
                to="/you/steps"
              />
              <NavRow icon={I.bell} title="Alerts" subtitle={alertsValue} to="/you/alerts" />
              {foodOn(S) ? (
                <NavRow
                  icon={I.glass}
                  title="Water reminders"
                  subtitle={remindersSummary(S, todayStr())}
                  to="/you/reminders/water"
                />
              ) : null}
            </RowGroup>

            <section className="k-group">
              <h2 className="k-group__h">Appearance</h2>
              <div className="you-stack">
                <Segmented<NonNullable<ProfileT["theme"]>>
                  ariaLabel="Appearance"
                  value={theme}
                  options={[
                    { value: "system", label: "System" },
                    { value: "light", label: "Light" },
                    { value: "dark", label: "Dark" },
                  ]}
                  onChange={(t) =>
                    update((d) => {
                      d.profile.theme = t;
                    })
                  }
                />
                <div className="k-rows">
                  {/* Text size is per phone (kit §40), so it reads LocalMeta, not the profile. */}
                  <NavRow
                    icon={I.phone}
                    title="Display"
                    subtitle={TEXT_SIZE_LABEL[meta.textSize ?? "normal"]}
                    to="/you/display"
                  />
                </div>
              </div>
            </section>

            <RowGroup heading="Account">
              <NavRow
                icon={I.user}
                title="Account & data"
                // a request to read this account waits there for an answer, so the row says so
                subtitle={asking ? `${nameOf(asking)} asks to read your plan` : syncValue}
                tone={asking ? "brand" : "default"}
                to="/you/account"
              />
              <NavRow icon={I.user} title="Family" subtitle={familyValue} to="/you/family" />
            </RowGroup>

            <RowGroup ariaLabel="About">
              <NavRow
                icon={I.info}
                title="About Kaya"
                subtitle="Version, and where the food values come from"
                to="/you/about"
              />
            </RowGroup>

            {/* The switch stays visible in user view on purpose — it is the way back, and a preview
                you cannot leave without a private window is the thing this replaces. */}
            {user?.role === "admin" ? (
              <section className="k-group">
                <h2 className="k-group__h">Admin</h2>
                <div className="you-disclose-stack">
                  {/* Only an admin has the endpoints behind this; the row would 404 for anyone else. */}
                  {isAdmin ? (
                    <div className="k-rows">
                      <NavRow
                        icon={I.user}
                        title="People"
                        subtitle={
                          people
                            ? `${people.all} ${people.all === 1 ? "account" : "accounts"}${
                                people.waiting ? ` · ${people.waiting} waiting` : ""
                              }`
                            : "Approve and manage"
                        }
                        to="/you/people"
                      />
                    </div>
                  ) : null}
                  <ChoiceField label="View as">
                    <Segmented<"admin" | "user">
                      ariaLabel="View as"
                      value={viewAsUser ? "user" : "admin"}
                      options={[
                        { value: "admin", label: "Admin" },
                        { value: "user", label: "User" },
                      ]}
                      onChange={(v) => setViewAsUser(v === "user")}
                    />
                    <p className="k-field__hint">
                      {viewAsUser
                        ? "Admin screens are hidden. This changes what you see, not what your account may do."
                        : "Hide the admin screens to see Kaya as everyone else does."}
                    </p>
                  </ChoiceField>
                </div>
              </section>
            ) : null}
          </div>
        </Screen>
      </IonContent>
    </IonPage>
  );
}
